import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Settings, CheckCircle, AlertCircle, Eye, EyeOff, Info, Cog, TestTube, Phone, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Reuse the existing PlaceholderModal from AIAgentConfig
const PlaceholderModal = ({ isOpen, onClose, onInsertPlaceholder, placeholderType = 'sms' }) => {
  const [globalPlaceholders, setGlobalPlaceholders] = React.useState([]);
  const [businessPlaceholders, setBusinessPlaceholders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch placeholders when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen]);

  const fetchPlaceholders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch global placeholders
      const globalResponse = await fetch(`${API_BASE_URL}/api/global-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch business placeholders
      const businessResponse = await fetch(`${API_BASE_URL}/api/business-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (globalResponse.ok) {
        const globalData = await globalResponse.json();
        setGlobalPlaceholders(globalData);
      }

      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        setBusinessPlaceholders(businessData);
      }
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Combine and categorize placeholders
  const getPlaceholders = () => {
    const allPlaceholders = [];

    // Add global placeholders
    globalPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Global',
        value: placeholder.value
      });
    });

    // Add business placeholders
    businessPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Custom',
        value: placeholder.value
      });
    });

    return allPlaceholders;
  };

  const placeholders = getPlaceholders();
  const categories = [...new Set(placeholders.map(p => p.category))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Sticky Header */}
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Available Placeholders</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-white opacity-90 mt-1">
            Click "Insert" to add a placeholder into your template
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-primary)' }}></div>
              <p className="text-gray-600">Loading placeholders...</p>
            </div>
          ) : placeholders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No placeholders available. Create some in Business Info or CMS Settings.</p>
            </div>
          ) : (
            categories.map(category => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">{category}</h4>
                <div className="space-y-2">
                  {placeholders
                    .filter(placeholder => placeholder.category === category)
                    .map(placeholder => (
                      <div key={placeholder.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div className="flex-1 space-y-1">
                          <div>
                            <code className="text-sm font-mono bg-white px-2 py-1 rounded border" style={{ color: 'var(--theme-primary)' }}>
                              {placeholder.code}
                            </code>
                          </div>
                          {placeholder.value && (
                            <div className="text-xs text-gray-600">
                              {placeholder.value}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => onInsertPlaceholder(placeholder.code)}
                          className="ml-3 px-3 py-1 text-white text-sm rounded transition-colors"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                        >
                          Insert
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SMSConfiguration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [smsConfig, setSmsConfig] = useState({
    notification_phone: '',
    sms_provider: 'twilio', // 'twilio' or 'sendgrid'
    is_enabled: false,
    // Twilio fields
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    // SendGrid fields (for SMS)
    sendgrid_api_key: '',
    sender_phone: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Template management state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    content: '',
    description: '',
    is_enabled: true,
    chatgpt_enabled: true
  });
  const [scrollPosition, setScrollPosition] = useState(0);

  // Placeholder Modal State
  const [placeholderModal, setPlaceholderModal] = useState({
    isOpen: false,
    targetField: null,
    updateFunction: null,
    targetElement: null
  });

  const { user, token, isAdmin, canAccessManager, loading: authLoading } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const tabs = [
    { id: 'general', label: 'General Settings', icon: Settings },
    { id: 'provider', label: 'SMS Provider', icon: MessageSquare },
    { id: 'templates', label: 'SMS Templates', icon: MessageSquare },
    { id: 'test', label: 'Test & Verify', icon: TestTube }
  ];

  const fetchSmsConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSmsConfig(data);
      } else {
        throw new Error('Failed to fetch SMS configuration');
      }
    } catch (err) {
      setError('Failed to load SMS configuration');
      console.error('Error fetching SMS config:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (token && !user) return;

    if (user && canAccessManager()) {
      setError(null);
      fetchSmsConfig();
    } else if (user && !canAccessManager()) {
      setError('Access denied. Manager or admin privileges required.');
      setLoading(false);
    } else if (!token) {
      // Redirect to login page for unauthenticated users
      navigate('/login');
    }
  }, [user, token, authLoading]);

  // Load templates when templates tab is active
  useEffect(() => {
    if (activeTab === 'templates' && user && canAccessManager()) {
      fetchTemplates();
    }
  }, [activeTab, user, token]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmsConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(smsConfig)
      });

      if (response.ok) {
        setSuccess('SMS configuration saved successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save SMS configuration');
      }
    } catch (err) {
      console.error('Error saving SMS config:', err);
      setError(err.message || 'Failed to save SMS configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    if (!smsConfig.notification_phone) {
      setError('Please enter a notification phone number to test');
      return;
    }

    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-config/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          test_phone: smsConfig.notification_phone
        })
      });

      if (response.ok) {
        setSuccess(`Test SMS sent successfully to ${smsConfig.notification_phone}!`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test SMS');
      }
    } catch (err) {
      console.error('Error testing SMS:', err);
      setError(err.message || 'Failed to send test SMS');
    } finally {
      setTesting(false);
    }
  };

  // Template management functions
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates?template_type=sms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched SMS Templates:', data); // Debug log
        // Check for the specific "Campaign SMS" template
        const campaignTemplate = data.find(t => t.name === 'Campaign SMS');
        if (campaignTemplate) {
          console.log('Campaign SMS Template:', campaignTemplate);
          console.log('ChatGPT Enabled value:', campaignTemplate.chatgpt_enabled, typeof campaignTemplate.chatgpt_enabled);
        }
        setTemplates(data);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (err) {
      setError('Failed to load SMS templates');
      console.error('Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      setError('Template name and content are required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...templateForm,
          type: 'sms',
          created_by: user?.email || 'unknown'
        })
      });

      if (response.ok) {
        setSuccess('SMS template created successfully!');
        setTemplateForm({ name: '', content: '', description: '', is_enabled: true, chatgpt_enabled: true });
        setShowTemplateModal(false);
        fetchTemplates();
        restoreScrollPosition(); // Restore scroll position after successful creation
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create template');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const restoreScrollPosition = () => {
    // Small delay to ensure DOM is updated after modal closes
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 100);
  };

  const handleUpdateTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      setError('Template name and content are required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        setSuccess('SMS template updated successfully!');
        setTemplateForm({ name: '', content: '', description: '', is_enabled: true, chatgpt_enabled: true });
        setEditingTemplate(null);
        setShowTemplateModal(false);
        fetchTemplates();
        restoreScrollPosition(); // Restore scroll position after successful save
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update template');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('SMS template deleted successfully!');
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete template');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddTemplate = () => {
    // Store current scroll position before opening modal
    setScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    // Store current scroll position before opening modal
    setScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
    
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      content: template.content,
      description: template.description || '',
      is_enabled: template.is_enabled !== false, // Default to true if null or undefined
      chatgpt_enabled: Boolean(template.chatgpt_enabled) // Convert to proper boolean, defaults to false for undefined
    });
    setShowTemplateModal(true);
  };

  const handleCancelTemplateForm = () => {
    setTemplateForm({ name: '', content: '', description: '', is_enabled: true, chatgpt_enabled: true });
    setEditingTemplate(null);
    setShowTemplateModal(false);
    restoreScrollPosition(); // Restore scroll position when cancelling
  };

  // Placeholder Modal Functions
  const openPlaceholderModal = (targetField, updateFunction, targetElement = null) => {
    setPlaceholderModal({
      isOpen: true,
      targetField,
      updateFunction,
      targetElement: targetElement || document.activeElement
    });
  };

  const closePlaceholderModal = () => {
    setPlaceholderModal({
      isOpen: false,
      targetField: null,
      updateFunction: null,
      targetElement: null
    });
  };

  const insertPlaceholder = (placeholderCode) => {
    if (placeholderModal.updateFunction && placeholderModal.targetField) {
      // Regular form field handling
      if (placeholderModal.targetElement && 
          (placeholderModal.targetElement.tagName === 'TEXTAREA' || 
           placeholderModal.targetElement.tagName === 'INPUT')) {
        
        const element = placeholderModal.targetElement;
        const currentValue = element.value;
        const cursorPosition = element.selectionStart || currentValue.length;
        
        // Insert at cursor position
        const newValue = currentValue.slice(0, cursorPosition) + 
                         placeholderCode + 
                         currentValue.slice(cursorPosition);
        
        // Update the state with the new value
        placeholderModal.updateFunction(prev => ({
          ...prev,
          [placeholderModal.targetField]: newValue
        }));
        
        // Set cursor position after the inserted placeholder
        setTimeout(() => {
          if (element) {
            element.focus();
            element.setSelectionRange(cursorPosition + placeholderCode.length, cursorPosition + placeholderCode.length);
          }
        }, 0);
        
      } else {
        // Fallback: append to the end if no valid target element
        placeholderModal.updateFunction(prev => ({
          ...prev,
          [placeholderModal.targetField]: prev[placeholderModal.targetField] + placeholderCode
        }));
      }
    }
    closePlaceholderModal();
  };

  // handleToggleTemplate function removed - enable/disable moved to edit form

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading SMS configuration...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-3" />
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    SMS Configuration
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Success Alert */}
            {success && (
              <div className="mb-6 rounded-md p-4 bg-green-50 border border-green-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      {success}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-6 rounded-md p-4 bg-red-50 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <form onSubmit={handleSubmit} className="p-3 sm:p-6">
                {/* General Settings Tab */}
                <div className={activeTab === 'general' ? 'block' : 'hidden'}>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-3">General Settings</h3>
                      
                      {/* Enable SMS Notifications */}
                      <div className="flex items-center mb-4">
                        <input
                          type="checkbox"
                          name="is_enabled"
                          id="is_enabled"
                          checked={smsConfig.is_enabled}
                          onChange={handleInputChange}
                          className="h-4 w-4 border-gray-300 rounded"
                          style={{ 
                            accentColor: 'var(--theme-primary)',
                            color: 'var(--theme-primary)'
                          }}
                        />
                        <label htmlFor="is_enabled" className="ml-3 text-sm text-gray-900">
                          Enable SMS notifications
                        </label>
                      </div>

                      {/* Notification Phone */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          <Phone className="h-3.5 w-3.5 inline mr-1.5" />
                          Notification Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="notification_phone"
                          value={smsConfig.notification_phone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+1234567890"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This phone number will receive system notifications and test messages
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SMS Provider Tab */}
                <div className={activeTab === 'provider' ? 'block' : 'hidden'}>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-3">SMS Provider</h3>
                      
                      {/* Provider Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Choose SMS Service Provider
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="sms_provider"
                              id="twilio"
                              value="twilio"
                              checked={smsConfig.sms_provider === 'twilio'}
                              onChange={handleInputChange}
                              className="h-4 w-4 border-gray-300"
                              style={{ 
                                accentColor: 'var(--theme-primary)',
                                color: 'var(--theme-primary)'
                              }}
                            />
                            <label htmlFor="twilio" className="ml-2.5 text-sm text-gray-900">
                              Twilio SMS <span className="text-xs text-gray-500">(Recommended for reliable delivery)</span>
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="sms_provider"
                              id="sendgrid"
                              value="sendgrid"
                              checked={smsConfig.sms_provider === 'sendgrid'}
                              onChange={handleInputChange}
                              className="h-4 w-4 border-gray-300"
                              style={{ 
                                accentColor: 'var(--theme-primary)',
                                color: 'var(--theme-primary)'
                              }}
                            />
                            <label htmlFor="sendgrid" className="ml-2.5 text-sm text-gray-900">
                              SendGrid SMS <span className="text-xs text-gray-500">(Alternative option)</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Twilio Configuration */}
                      {smsConfig.sms_provider === 'twilio' && (
                        <div className="space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                              <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-theme-blue" />
                              <div className="text-xs text-gray-800">
                                <p className="font-medium mb-1.5 text-gray-900">Twilio Setup Instructions:</p>
                                <ol className="list-decimal list-inside space-y-0.5 text-gray-700">
                                  <li>Create a Twilio account at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">twilio.com</a></li>
                                  <li>Get your Account SID and Auth Token from the Console Dashboard</li>
                                  <li>Purchase a phone number from Twilio for sending SMS</li>
                                  <li>Enter your credentials below</li>
                                </ol>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Account SID
                            </label>
                            <input
                              type="text"
                              name="twilio_account_sid"
                              value={smsConfig.twilio_account_sid}
                              onChange={handleInputChange}
                              required={smsConfig.sms_provider === 'twilio'}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Auth Token
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                name="twilio_auth_token"
                                value={smsConfig.twilio_auth_token}
                                onChange={handleInputChange}
                                required={smsConfig.sms_provider === 'twilio'}
                                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Your Twilio Auth Token"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Twilio Phone Number
                            </label>
                            <input
                              type="tel"
                              name="twilio_phone_number"
                              value={smsConfig.twilio_phone_number}
                              onChange={handleInputChange}
                              required={smsConfig.sms_provider === 'twilio'}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="+1234567890"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Your Twilio phone number (sender ID)
                            </p>
                          </div>
                        </div>
                      )}

                      {/* SendGrid Configuration */}
                      {smsConfig.sms_provider === 'sendgrid' && (
                        <div className="space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                              <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-theme-blue" />
                              <div className="text-xs text-gray-800">
                                <p className="font-medium mb-1.5 text-gray-900">SendGrid SMS Setup Instructions:</p>
                                <ol className="list-decimal list-inside space-y-0.5 text-gray-700">
                                  <li>Create a SendGrid account at <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">sendgrid.com</a></li>
                                  <li>Generate an API key in your SendGrid dashboard</li>
                                  <li>Configure SMS settings in your SendGrid account</li>
                                  <li>Enter your credentials below</li>
                                </ol>
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              SendGrid API Key
                            </label>
                            <div className="relative">
                              <input
                                type={showApiKey ? "text" : "password"}
                                name="sendgrid_api_key"
                                value={smsConfig.sendgrid_api_key}
                                onChange={handleInputChange}
                                required={smsConfig.sms_provider === 'sendgrid'}
                                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="SG.xxxxxxxxxx"
                              />
                              <button
                                type="button"
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showApiKey ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Sender Phone Number
                            </label>
                            <input
                              type="tel"
                              name="sender_phone"
                              value={smsConfig.sender_phone}
                              onChange={handleInputChange}
                              required={smsConfig.sms_provider === 'sendgrid'}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="+1234567890"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Phone number to send SMS from
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SMS Templates Tab */}
                <div className={activeTab === 'templates' ? 'block' : 'hidden'}>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">SMS Templates</h3>
                          <p className="text-xs text-gray-600">Create and manage SMS templates with placeholders</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddTemplate}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white transition-colors"
                          style={{ backgroundColor: '#10b981' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          Add Template
                        </button>
                      </div>

                      {/* Template Form Modal */}
                      {showTemplateModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                            {/* Sticky Header */}
                            <div className="px-6 py-4 border-b border-gray-200 rounded-t-lg" style={{ backgroundColor: 'var(--theme-primary)' }}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="text-xl font-semibold text-white">
                                    {editingTemplate ? 'Edit SMS Template' : 'Create New SMS Template'}
                                  </h3>
                                  <p className="text-white text-opacity-80 text-sm mt-1">
                                    Create and manage SMS templates with placeholders (160 character limit)
                                  </p>
                                </div>
                                <button
                                  onClick={handleCancelTemplateForm}
                                  className="text-white hover:text-gray-200 transition-colors p-2"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            {/* Scrollable Content Area */}
                            <div className="flex-1 overflow-y-auto px-6 py-6">
                              <div className="space-y-6">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Template Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={templateForm.name}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                    style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                                    placeholder="Enter template name"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={templateForm.description}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                    style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                                    placeholder="Optional description for this template"
                                  />
                                </div>

                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      SMS Content * <span className="text-gray-500 text-xs">(160 char limit)</span>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        const textarea = e.target.closest('.flex').parentNode.querySelector('textarea');
                                        openPlaceholderModal('content', setTemplateForm, textarea);
                                      }}
                                      className="px-3 py-1 text-xs font-medium text-white rounded transition-colors"
                                      style={{ backgroundColor: 'var(--theme-primary)' }}
                                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                    >
                                      Available Placeholders
                                    </button>
                                  </div>
                                  <textarea
                                    value={templateForm.content}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                                    style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                                    placeholder="Enter SMS content. Use placeholders like [CUSTOMER_NAME], [PET_NAME], [PHONE_NUMBER], etc."
                                    maxLength={160}
                                  />
                                  <div className="text-xs text-gray-500 mt-1">
                                    {templateForm.content.length}/160 characters
                                  </div>
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id="template-enabled"
                                      checked={templateForm.is_enabled !== false} // Default to true if not set
                                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_enabled: e.target.checked }))}
                                      className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="template-enabled" className="ml-2 block text-sm font-medium text-blue-800">
                                      Enable this template for Main Dashboard appointment reminders
                                    </label>
                                  </div>
                                  <p className="text-xs text-theme-blue">
                                    When enabled, this template will appear as an option in the Main Dashboard SMS appointment reminder widget.
                                  </p>
                                  
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id="template-chatgpt-enabled"
                                      checked={templateForm.chatgpt_enabled === true} // Explicitly check for true
                                      onChange={(e) => setTemplateForm(prev => ({ ...prev, chatgpt_enabled: e.target.checked }))}
                                      className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="template-chatgpt-enabled" className="ml-2 block text-sm font-medium text-blue-800">
                                      Enable ChatGPT for this template
                                    </label>
                                  </div>
                                  <p className="text-xs text-theme-blue">
                                    When enabled, this template will appear in Holiday and Recurring mode SMS agent forms that use ChatGPT for content generation.
                                  </p>
                                </div>

                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-amber-800 mb-2">📱 SMS Best Practices:</h4>
                                  <ul className="text-xs text-amber-700 space-y-1">
                                    <li>• Keep messages under 160 characters for single SMS</li>
                                    <li>• Use clear, concise language</li>
                                    <li>• Include a clear call-to-action</li>
                                    <li>• Always include business name and contact info</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Sticky Footer */}
                            <div className="px-6 py-4 border-t border-gray-200 rounded-b-lg bg-gray-50">
                              <div className="flex items-center justify-end space-x-3">
                                <button
                                  type="button"
                                  onClick={handleCancelTemplateForm}
                                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                                  className="px-6 py-2 text-white rounded-lg transition-colors font-medium"
                                  style={{ backgroundColor: 'var(--theme-primary)' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                                >
                                  {editingTemplate ? 'Update Template' : 'Create Template'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Templates List */}
                      <div>
                        {loadingTemplates ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500 mx-auto"></div>
                            <p className="text-gray-500 mt-2">Loading templates...</p>
                          </div>
                        ) : templates.length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h4 className="text-lg font-medium text-gray-900 mb-2">No SMS Templates</h4>
                            <p className="text-gray-500">Create your first SMS template to get started.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {templates.map(template => (
                              <div key={template.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center flex-wrap gap-2 mb-1">
                                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                                      {template.is_enabled !== false && (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                          Main Dashboard Enabled
                                        </span>
                                      )}
                                      {template.chatgpt_enabled === true && (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                          ChatGPT
                                        </span>
                                      )}
                                    </div>
                                    {template.description && (
                                      <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                                    )}
                                    <div className="mt-2">
                                      <p className="text-sm text-gray-700">{template.content}</p>
                                    </div>
                                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                      <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                                      {template.updated_at && template.updated_at !== template.created_at && (
                                        <span>Updated: {new Date(template.updated_at).toLocaleDateString()}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2 ml-4">
                                    <button
                                      onClick={() => handleEditTemplate(template)}
                                      className="text-theme-blue hover:text-blue-800 text-sm"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTemplate(template.id)}
                                      className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Available Placeholders Info section removed */}
                    </div>
                  </div>
                </div>

                {/* Test & Verify Tab */}
                <div className={activeTab === 'test' ? 'block' : 'hidden'}>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-3">Test & Verify SMS Configuration</h3>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Current Configuration Status</h4>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">SMS Enabled:</span>
                            <span className={`text-xs font-medium ${smsConfig.is_enabled ? 'text-green-600' : 'text-red-600'}`}>
                              {smsConfig.is_enabled ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Provider:</span>
                            <span className="text-xs font-medium text-gray-900 capitalize">{smsConfig.sms_provider}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Notification Phone:</span>
                            <span className="text-xs font-medium text-gray-900">{smsConfig.notification_phone || 'Not set'}</span>
                          </div>
                        </div>
                      </div>

                      {smsConfig.is_enabled && smsConfig.notification_phone ? (
                        <div>
                          <p className="text-xs text-gray-600 mb-3">
                            Click the button below to send a test SMS to your notification phone number to verify your configuration is working correctly.
                          </p>
                          <button
                            type="button"
                            onClick={handleTestSms}
                            disabled={testing}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                            style={{ backgroundColor: testing ? '#94a3b8' : '#10b981' }}
                            onMouseEnter={(e) => {
                              if (!testing) e.target.style.backgroundColor = '#059669';
                            }}
                            onMouseLeave={(e) => {
                              if (!testing) e.target.style.backgroundColor = '#10b981';
                            }}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {testing ? 'Sending...' : 'Send Test SMS'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-xs text-red-600 mb-3">
                            Please configure and enable SMS notifications first.
                          </p>
                          <div className="text-xs text-gray-500">
                            <p className="font-medium mb-1">Required:</p>
                            <ul className="list-disc list-inside space-y-0.5">
                              <li>Enable SMS notifications</li>
                              <li>Set notification phone number</li>
                              <li>Configure SMS provider settings</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: saving ? '#94a3b8' : '#10b981' }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#059669';
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#10b981';
                    }}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Saving Modal */}
      {saving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Saving Configuration</h3>
            <p className="text-gray-600">Please wait while we save your SMS template settings...</p>
          </div>
        </div>
      )}

      {/* Placeholder Modal */}
      <PlaceholderModal
        isOpen={placeholderModal.isOpen}
        onClose={closePlaceholderModal}
        onInsertPlaceholder={insertPlaceholder}
        placeholderType="sms"
      />
    </div>
  );
};

export default SMSConfiguration;